import React, { useState, useEffect, useRef } from 'react';
import { InputSwitch, InputSwitchChangeEvent } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import TooltipIcon from '../tooltip-icon/tooltip-icon.component';

interface PixelInputProps {
  title?: string;
  tooltipMessage?: string;
  initialPixel?: string;
  onSave: (value: string | null) => void;
  showToggle?: boolean;
  disabled?: boolean;
}

export const PixelInput: React.FC<PixelInputProps> = ({
  title,
  tooltipMessage,
  initialPixel,
  onSave,
  showToggle = true,
  disabled = false,
}) => {
  const [usePixel, setUsePixel] = useState(!showToggle || Boolean(initialPixel));
  const [pixelValue, setPixelValue] = useState(initialPixel || '');
  const [actualLines, setActualLines] = useState(1);
  const lineNumbersRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPixelValue(initialPixel || '');
  }, [initialPixel]);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = '0px';
      const scrollHeight = textarea.scrollHeight;
      textarea.style.height = `${scrollHeight}px`;
    }
    if (textareaRef.current) {
      const lineHeight = 23;
      const contentHeight = textareaRef.current.scrollHeight;
      const lines = Math.max(1, Math.ceil(contentHeight / lineHeight));
      setActualLines(lines);
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [initialPixel, pixelValue]);

  const handleToggle = ({ value }: InputSwitchChangeEvent) => {
    setUsePixel(value);
    if (!value) {
      setPixelValue('');
      onSave(null);
    }
  };

  const handlePixelChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setPixelValue(value);
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    if (lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = scrollTop;
    }
    if (containerRef.current) {
      containerRef.current.scrollTop = scrollTop;
    }
  };

  const handleClear = () => {
    setPixelValue('');
    onSave(null);
    setUsePixel(true);
  };

  const hasChanges = pixelValue !== (initialPixel || '');

  const handleContainerClick = () => {
    if (textareaRef.current && !disabled && !initialPixel) {
      textareaRef.current.focus();
    }
  };

  return (
    <div>
      {showToggle && (
        <div className="flex flex-row items-center mb-4">
          <div className="text-2xl mr-2">
            {title}
          </div>
          <InputSwitch
            checked={usePixel}
            onChange={handleToggle}
            className={`custom-switch ${usePixel ? 'p-inputswitch-checked' : ''}`}
          />
          {tooltipMessage && (
            <TooltipIcon
              targetClass="pixel-toggle"
              message={tooltipMessage}
            />
          )}
        </div>
      )}
      {(usePixel || !showToggle) && (
        <div className="relative">
          <div
            className={`relative flex border border-gray-300 rounded-lg shadow-sm h-32 overflow-hidden ${!disabled && !initialPixel ? 'cursor-text' : ''}`}
            onClick={handleContainerClick}
          >
            <div className="absolute top-2 right-3 text-gray-400 text-sm font-mono z-10 pointer-events-none tracking-[-0.1em] font-extrabold select-none">
              {'</>'}
            </div>
            <div
              ref={lineNumbersRef}
              onScroll={handleScroll}
              className="px-2 pt-4 pb-6 text-right text-sm text-white/80 border-r border-gray-200 font-mono overflow-y-auto bg-[#bababa] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] select-none min-w-[2rem] whitespace-pre leading-[1.6] tracking-[0.3px]"
            >
              {Array.from({ length: actualLines }, (_, i) =>
                String((i + 1) % 100).padStart(2, '0')
              ).join('\n')}
            </div>
            <div
              ref={containerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto p-4 bg-white [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            >
              <textarea
                ref={textareaRef}
                value={pixelValue}
                onChange={handlePixelChange}
                className={`w-full font-mono h-[22px] text-sm text-gray-600 border-0 m-0 p-0 focus:ring-0 focus:outline-none resize-none leading-[1.6] tracking-[0.3px] ${disabled || Boolean(initialPixel) ? 'cursor-default' : 'cursor-text'}`}
                placeholder={disabled ? "No pixel set." : "Paste your pixel code here..."}
                readOnly={Boolean(initialPixel) || disabled}
              />
            </div>
          </div>

          <div className="absolute bottom-4 right-4 flex gap-2">
            {!disabled && (
              initialPixel ? (
                <button
                  onClick={handleClear}
                  className="text-sm text-gray-500 hover:text-gray-700 bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded-full relative z-10 transition-colors"
                >
                  Clear
                </button>
              ) : hasChanges && (
                <Button
                  label={"Save"}
                  onClick={() => onSave(pixelValue || null)}
                  className="p-button-sm rounded-md px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-colors"
                />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};